import { Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { formatElapsedTime, stageProgressPercent } from "@/lib/onboarding/summaryProgress";

const THINKING_PHRASES = [
  "Analysing your response…",
  "Thinking through your answer…",
  "Reviewing what you shared…",
  "Preparing the next question…",
  "Processing your input…",
  "Considering your response…",
];

// Shown before the user has answered anything — answer-centric phrases would be wrong.
const INITIAL_THINKING_PHRASES = [
  "Setting up your onboarding…",
  "Preparing your first question…",
  "Getting things ready…",
];

const PHRASES: Record<TThinkingVariant, string[]> = {
  response: THINKING_PHRASES,
  initial: INITIAL_THINKING_PHRASES,
};

export type TThinkingVariant = "response" | "initial";

/** Between-question activity: the AI is composing the next question. */
export const ThinkingIndicator = ({ variant = "response" }: { variant?: TThinkingVariant }) => {
  const phrases = PHRASES[variant];
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      // Fade out, swap phrase, fade in
      setVisible(false);
      setTimeout(() => {
        setPhraseIndex((i) => (i + 1) % phrases.length);
        setVisible(true);
      }, 200);
    }, 2200);
    return () => clearInterval(interval);
  }, [phrases.length]);

  return (
    <div
      className="flex items-center gap-3 py-0.5"
      role="status"
      aria-live="polite"
    >
      {/* Pulsing activity dot */}
      <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-50 motion-reduce:animate-none" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary" />
      </span>

      {/* Rotating contextual phrase */}
      <span
        className="text-sm font-medium text-foreground transition-opacity duration-200"
        style={{ opacity: visible ? 1 : 0 }}
      >
        {phrases[phraseIndex]}
      </span>

      {/* Animated dots */}
      <span className="flex gap-1">
        <span
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/70 motion-reduce:animate-none"
          style={{ animationDuration: "0.9s" }}
        />
        <span
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/70 motion-reduce:animate-none"
          style={{ animationDuration: "0.9s", animationDelay: "180ms" }}
        />
        <span
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/70 motion-reduce:animate-none"
          style={{ animationDuration: "0.9s", animationDelay: "360ms" }}
        />
      </span>
    </div>
  );
};

/**
 * The three stages the backend actually reports, named from the user's side of the screen.
 * `current_stage` arrives as "starting" | "calling_ai" | "saving" | "completed".
 */
const SUMMARY_STAGES = [
  { key: "starting", label: "Reading your answers" },
  { key: "calling_ai", label: "Writing your summary" },
  { key: "saving", label: "Saving it to your profile" },
] as const;

const activeStageIndex = (stage?: string | null): number => {
  const normalised = (stage ?? "").replace(/\s+/g, "_").toLowerCase();
  if (normalised === "completed" || normalised === "saving") return 2;
  if (normalised === "calling_ai") return 1;
  return 0;
};

type TSummaryProgressProps = {
  stage?: string | null;
  elapsedSeconds?: number | null;
};

/**
 * In-chat progress for summary generation.
 *
 * Deliberately mirrors the readiness dashboard's ProcessingCard — same stage stepper, same
 * eased bar, same elapsed readout in tabular mono — so the wait reads as one product
 * behaviour rather than a one-off. Compressed to bubble scale and driven by the real
 * backend stage rather than a timer, so the stepper never claims progress that hasn't
 * happened.
 */
export const SummaryProgressIndicator = ({ stage, elapsedSeconds }: TSummaryProgressProps) => {
  const active = activeStageIndex(stage);
  const elapsed = formatElapsedTime(elapsedSeconds);
  const pct = stageProgressPercent(stage);

  return (
    <div
      className="flex flex-col gap-3"
      role="status"
      aria-live="polite"
    >
      {/* Eyebrow — says what this bubble is, so it reads as the system working rather than
          as something the assistant said. */}
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">Generating summary</span>
        {elapsed && <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">{elapsed}</span>}
      </div>

      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary motion-reduce:animate-none" />
        <p className="text-sm font-medium text-foreground">Building your company summary</p>
      </div>

      {/* Progress track driven by the reported stage. */}
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-primary/15"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Summary generation progress"
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-700 ease-out"
          style={{ width: `${Math.max(8, pct)}%` }}
          data-testid="summary-progress-bar"
        />
      </div>

      {/* Stage stepper — done / active / pending, from real backend state. */}
      <ol className="flex flex-col gap-1.5">
        {SUMMARY_STAGES.map(({ key, label }, i) => {
          const done = i < active;
          const isActive = i === active;
          return (
            <li
              key={key}
              className="flex items-center gap-2"
            >
              <span
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors ${
                  done
                    ? "bg-primary text-primary-foreground"
                    : isActive
                      ? "bg-primary/10 text-primary ring-1 ring-inset ring-primary/30"
                      : "bg-muted"
                }`}
              >
                {done ? (
                  <Check className="h-2.5 w-2.5" />
                ) : isActive ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin motion-reduce:animate-none" />
                ) : (
                  <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
                )}
              </span>
              <span
                className={`text-xs ${
                  done
                    ? "text-muted-foreground"
                    : isActive
                      ? "font-medium text-foreground"
                      : "text-muted-foreground/70"
                }`}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>

      <p className="text-xs text-muted-foreground">
        This usually takes under a minute. We'll open your summary as soon as it's ready.
      </p>
    </div>
  );
};

export default ThinkingIndicator;
